import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { formatTime12Hour } from '../../utils/timeFormat';
import { 
  DollarSign, Users, Calendar, Crown, TrendingUp, 
  Send, Loader2, Scissors, Ban, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { EngagementStatusBadge } from '../../components/EngagementStatusBadge';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsLoading, setSmsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [teamChatUnread, setTeamChatUnread] = useState(0);
  
  // SMS Campaign state
  const [recipientType, setRecipientType] = useState('all');
  const [smsMessage, setSmsMessage] = useState('');
  
  // Email Campaign state
  const [campaignType, setCampaignType] = useState('sms'); // 'sms' or 'email'
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  // Barber engagement filter
  const [showArchivedBarbers, setShowArchivedBarbers] = useState(false);

  // Block customer dialog state
  const [blockDialog, setBlockDialog] = useState({ open: false, customer: null });
  const [blockReason, setBlockReason] = useState('no_show_repeat');
  const [blockNote, setBlockNote] = useState('');
  const [blockSubmitting, setBlockSubmitting] = useState(false);

  // Stripe sync state
  const [stripeSyncing, setStripeSyncing] = useState(false);

  const syncStripeSubscriptions = async () => {
    if (!window.confirm('Pull all active subscriptions from Stripe and update your DB? This is safe to run anytime.')) return;
    try {
      setStripeSyncing(true);
      const res = await api.post('/api/subscriptions/sync-from-stripe');
      const d = res.data || {};
      toast.success(
        `Synced: ${d.updated} updated, ${d.linked_by_email} linked by email, ${d.skipped} skipped`
      );
      // Refresh stats
      const ts = Date.now();
      const statsRes = await api.get(`/api/admin/stats?_t=${ts}`);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setStripeSyncing(false);
    }
  };

  const openBlockDialog = (customer) => {
    setBlockReason('no_show_repeat');
    setBlockNote('');
    setBlockDialog({ open: true, customer });
  };

  const submitBlock = async () => {
    const c = blockDialog.customer;
    if (!c) return;
    try {
      setBlockSubmitting(true);
      await api.post(`/api/admin/customers/${c.id}/block`, {
        reason: blockReason,
        note: blockNote || null,
        is_guest: !!c.is_guest,
        phone: c.phone || null,
        name: c.name || null,
      });
      toast.success(`Blocked ${c.name}`);
      setBlockDialog({ open: false, customer: null });
      setCustomers((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, blocked: true, blocked_reason: blockReason } : x))
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to block customer');
    } finally {
      setBlockSubmitting(false);
    }
  };

  const unblockCustomer = async (c) => {
    if (!window.confirm(`Unblock ${c.name}? They will be able to book again.`)) return;
    try {
      const params = c.is_guest
        ? `?is_guest=true&phone=${encodeURIComponent(c.phone || '')}`
        : '';
      await api.post(`/api/admin/customers/${c.id}/unblock${params}`);
      toast.success(`Unblocked ${c.name}`);
      setCustomers((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, blocked: false, blocked_reason: null } : x))
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unblock customer');
    }
  };
  
  // Check admin access
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else if (currentUser.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Poll team-chat unread count for admin
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') return;
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await api.get('/api/team-chat/unread-count');
        if (!cancelled) setTeamChatUnread(res.data?.count || 0);
      } catch (_) { /* silent */ }
    };
    pull();
    const id = setInterval(() => { if (!document.hidden) pull(); }, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [currentUser]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Add cache-busting timestamp
        const timestamp = Date.now();
        
        // Fetch stats
        const statsRes = await api.get(`/api/admin/stats?_t=${timestamp}`);
        setStats(statsRes.data);
        
        // Fetch recent appointments
        const aptsRes = await api.get(`/api/admin/appointments?limit=50&_t=${timestamp}`);
        setAppointments(aptsRes.data.appointments);
        
        // Fetch customers
        const customersRes = await api.get(`/api/admin/customers?limit=100&_t=${timestamp}`);
        setCustomers(customersRes.data.customers);
        
        // Fetch barbers
        const barbersRes = await api.get(`/api/admin/barbers?include_archived=${showArchivedBarbers}&_t=${timestamp}`);
        setBarbers(barbersRes.data.barbers);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    if (currentUser?.role === 'admin') {
      fetchData();
    }
  }, [currentUser, showArchivedBarbers]);

  // Update barber engagement status override
  const handleEngagementStatusChange = async (barberId, newStatus) => {
    try {
      // newStatus === 'auto' means clear the override
      const payload = { status: newStatus === 'auto' ? null : newStatus };
      await api.patch(`/api/admin/barbers/${barberId}/engagement-status`, payload);
      toast.success(
        newStatus === 'auto'
          ? 'Status cleared — will auto-compute from activity'
          : `Status set to ${newStatus}`
      );
      // Re-fetch barbers list
      const ts = Date.now();
      const res = await api.get(`/api/admin/barbers?include_archived=${showArchivedBarbers}&_t=${ts}`);
      setBarbers(res.data.barbers);
    } catch (error) {
      console.error('Failed to update engagement status:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update status');
    }
  };
  
  // Handle SMS campaign
  const handleSendSMS = async () => {
    if (!smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSmsLoading(true);
    
    try {
      const response = await api.post('/api/admin/sms/send', {
        recipient_type: recipientType,
        message: smsMessage
      });
      
      toast.success(`SMS sent to ${response.data.successful} customers`, {
        description: response.data.failed > 0 ? `${response.data.failed} failed` : 'All sent successfully'
      });
      
      setSmsMessage('');
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error('Failed to send SMS campaign');
    } finally {
      setSmsLoading(false);
    }
  };
  
  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Please enter both subject and message');
      return;
    }
    
    setSmsLoading(true);
    
    try {
      const response = await api.post('/api/admin/email/send', {
        recipient_type: recipientType,
        subject: emailSubject,
        message: emailMessage
      });
      
      toast.success(`Email sent to ${response.data.successful} customers`, {
        description: response.data.failed > 0 ? `${response.data.failed} failed` : 'All sent successfully'
      });
      
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email campaign');
    } finally {
      setSmsLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-lime-400 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="bg-black text-white min-h-screen py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-zinc-400">Manage your barbershop business</p>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-zinc-800 overflow-x-auto">
          {['overview', 'appointments', 'customers', 'barbers', 'sms'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-4 font-semibold capitalize whitespace-nowrap ${
                activeTab === tab 
                  ? 'text-lime-400 border-b-2 border-lime-400' 
                  : 'text-zinc-500 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gradient-to-br from-lime-500/10 to-lime-600/10 border-lime-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Total Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-lime-400">${stats.total_revenue}</p>
                  <p className="text-xs text-zinc-300 mt-1">All time</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-purple-400">{stats.total_appointments}</p>
                  <p className="text-xs text-zinc-300 mt-1">Total bookings</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    Active Customers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-400">{stats.active_customers}</p>
                  <p className="text-xs text-zinc-300 mt-1">With bookings</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border-yellow-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center">
                    <Crown className="w-4 h-4 mr-2" />
                    Memberships
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-yellow-400">{stats.active_memberships}</p>
                  <p className="text-xs text-zinc-300 mt-1">Active members</p>
                  <Button
                    onClick={syncStripeSubscriptions}
                    disabled={stripeSyncing}
                    size="sm"
                    variant="outline"
                    className="mt-3 h-8 text-xs border-yellow-600 text-yellow-400 hover:bg-yellow-600 hover:text-black w-full"
                    data-testid="sync-stripe-btn"
                  >
                    {stripeSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                    {stripeSyncing ? 'Syncing…' : 'Sync from Stripe'}
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Pick a Number Game */}
              <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/10 border-orange-500/30 hover:border-orange-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/pick-number-game')}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12zm-1-9h2v6H9V7z"/>
                    </svg>
                    Pick a Number Game
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Create and manage weekly number picking contests
                  </p>
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/pick-number-game'); }}
                  >
                    Manage Game →
                  </Button>
                </CardContent>
              </Card>
              
              {/* Other Quick Actions can go here */}
              <Card className="bg-gradient-to-br from-pink-500/10 to-pink-600/10 border-pink-500/30 hover:border-pink-400 transition-all cursor-pointer relative"
                    onClick={() => navigate('/admin/team-chat')}
                    data-testid="admin-team-chat-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-pink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                    Team Chat
                    {teamChatUnread > 0 && (
                      <span
                        className="ml-2 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center"
                        data-testid="admin-team-chat-unread"
                      >
                        {teamChatUnread > 9 ? '9+' : teamChatUnread}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Private channel with your barbers
                  </p>
                  <Button
                    className="w-full bg-pink-500 hover:bg-pink-600 text-white font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/team-chat'); }}
                  >
                    Open Chat →
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-lime-500/10 to-lime-600/10 border-lime-500/30 hover:border-lime-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/2fa')}
                    data-testid="admin-2fa-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-lime-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                    Two-Factor Authentication
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Add an extra layer of security to your admin account
                  </p>
                  <Button
                    className="w-full bg-lime-500 hover:bg-lime-600 text-black font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/2fa'); }}
                  >
                    Manage 2FA →
                  </Button>
                </CardContent>
              </Card>

              {/* Promotions */}
              <Card className="bg-gradient-to-br from-purple-500/10 to-fuchsia-600/10 border-purple-500/30 hover:border-purple-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/promotions')}
                    data-testid="admin-promotions-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z"/>
                    </svg>
                    Promotions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Create promo codes & special offers for customers
                  </p>
                  <Button
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/promotions'); }}
                    data-testid="admin-promotions-btn"
                  >
                    Manage Promotions →
                  </Button>
                </CardContent>
              </Card>

              {/* Holidays */}
              <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border-cyan-500/30 hover:border-cyan-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/holidays')}
                    data-testid="admin-holidays-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    Holidays
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Mark federal holidays & half-days on the booking calendar
                  </p>
                  <Button
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/holidays'); }}
                    data-testid="admin-holidays-btn"
                  >
                    Manage Holidays →
                  </Button>
                </CardContent>
              </Card>

              {/* Password Reset */}
              <Card className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 border-amber-500/30 hover:border-amber-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/password-reset')}
                    data-testid="admin-password-reset-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-amber-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 11-4 0 2 2 0 014 0zm6 5a9 9 0 11-18 0 9 9 0 0118 0zM12 14v.01M12 17h.01M9 11a3 3 0 016 0c0 1.657-1.343 2-3 3"/>
                    </svg>
                    Reset User Password
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Override any user's password — use for locked-out barbers, staff, or customers.
                  </p>
                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/password-reset'); }}
                    data-testid="admin-password-reset-btn"
                  >
                    Reset a Password →
                  </Button>
                </CardContent>
              </Card>

              {/* Shop Settings */}
              <Card className="bg-gradient-to-br from-purple-500/10 to-pink-600/10 border-purple-500/30 hover:border-purple-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/settings')}
                    data-testid="admin-settings-card">                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-purple-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    Shop Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Booking cutoff time, global rules and shop-wide preferences.
                  </p>
                  <Button
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/settings'); }}
                    data-testid="admin-settings-btn"
                  >
                    Configure →
                  </Button>
                </CardContent>
              </Card>

              {/* Adjust Points */}
              <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30 hover:border-yellow-400 transition-all cursor-pointer"
                    onClick={() => navigate('/staff/points-adjust')}
                    data-testid="admin-points-adjust-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-yellow-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.075 10.1c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                    </svg>
                    Adjust Points
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Forgot to mark complete? Bonus pts? Add or remove with one tap.
                  </p>
                  <Button
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/staff/points-adjust'); }}
                    data-testid="admin-points-adjust-btn"
                  >
                    Adjust Points →
                  </Button>
                </CardContent>
              </Card>


              {/* Merch Manager */}
              <Card className="bg-gradient-to-br from-lime-500/10 to-emerald-600/10 border-lime-500/30 hover:border-lime-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/merch')}
                    data-testid="admin-merch-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-lime-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
                    </svg>
                    Merch Manager
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Upload, edit, and price items shown on the customer Merch page.
                  </p>
                  <Button
                    className="w-full bg-lime-400 hover:bg-lime-500 text-black font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/merch'); }}
                    data-testid="admin-merch-btn"
                  >
                    Manage Merch →
                  </Button>
                </CardContent>
              </Card>

              {/* Push Notifications */}
              <Card className="bg-gradient-to-br from-fuchsia-500/10 to-pink-600/10 border-fuchsia-500/30 hover:border-fuchsia-400 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/push')}
                    data-testid="admin-push-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <svg className="w-6 h-6 mr-2 text-fuchsia-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                    </svg>
                    Push Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 mb-4">
                    Send announcements, deals, and reminders to customers
                  </p>
                  <Button
                    className="w-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate('/admin/push'); }}
                    data-testid="admin-push-btn"
                  >
                    Send Notification →
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            {/* Period Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-lime-400" />
                    Today
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white">Revenue:</span>
                      <span className="font-semibold text-lime-400">${stats.today_revenue}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Appointments:</span>
                      <span className="font-semibold text-white">{stats.today_appointments}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <TrendingUp className="w-5 h-5 mr-2 text-purple-400" />
                    This Week
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white">Revenue:</span>
                      <span className="font-semibold text-purple-400">${stats.week_revenue}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Appointments:</span>
                      <span className="font-semibold text-white">{stats.week_appointments}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center text-white">
                    <TrendingUp className="w-5 h-5 mr-2 text-blue-400" />
                    This Month
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white">Revenue:</span>
                      <span className="font-semibold text-blue-400">${stats.month_revenue}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Appointments:</span>
                      <span className="font-semibold text-white">{stats.month_appointments}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        
        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">All Appointments ({appointments.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-2 text-white font-semibold">Date</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Time</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Customer</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Barber</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Service</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Price</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-8 text-zinc-400">
                          No appointments found
                        </td>
                      </tr>
                    ) : (
                      appointments.map((apt, index) => (
                        <tr key={index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="py-3 px-2 text-white">{apt.date || 'N/A'}</td>
                          <td className="py-3 px-2 text-white">{formatTime12Hour(apt.time) || 'N/A'}</td>
                          <td className="py-3 px-2 text-white">{apt.customer_name || 'Unknown'}</td>
                          <td className="py-3 px-2 text-white">{apt.barber_name || 'N/A'}</td>
                          <td className="py-3 px-2 text-white">{apt.service_name || 'N/A'}</td>
                          <td className="py-3 px-2 text-lime-400 font-semibold">${apt.service_price || 0}</td>
                          <td className="py-3 px-2">
                            <Badge className={
                              apt.status === 'completed' ? 'bg-lime-500/20 text-lime-400' :
                              apt.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                              apt.status === 'arrived' ? 'bg-purple-500/20 text-purple-400' :
                              apt.status === 'no-show' ? 'bg-red-500/20 text-red-400' :
                              'bg-zinc-700 text-zinc-400'
                            }>
                              {apt.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Customer Insights ({customers.length})</CardTitle>
              <p className="text-sm text-zinc-400 mt-1">Showing all {customers.length} customers</p>
            </CardHeader>
            <CardContent>
              {/* Mobile card view (visible <md) */}
              <div className="space-y-3 md:hidden">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3"
                    data-testid={`customer-card-${customer.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center flex-wrap gap-1.5">
                          <span className="font-semibold text-white truncate">{customer.name}</span>
                          {customer.is_guest && (
                            <Badge className="bg-zinc-700 text-zinc-300 text-[10px] px-1.5 py-0">Guest</Badge>
                          )}
                          {customer.membership_tier === 'vip' && (
                            <Badge className="bg-gradient-to-r from-lime-500 to-lime-600 text-black text-[10px]">
                              <Crown className="w-2.5 h-2.5 mr-0.5" />VIP
                            </Badge>
                          )}
                          {customer.membership_tier === 'basic' && (
                            <Badge className="bg-purple-600 text-lime-400 text-[10px]">Regular</Badge>
                          )}
                          {customer.blocked && (
                            <Badge className="bg-red-600/80 text-white text-[10px]">
                              <Ban className="w-2.5 h-2.5 mr-0.5" />Blocked
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1 truncate">
                          {customer.email || <span className="italic text-zinc-500">(no email)</span>}
                        </div>
                        <div className="text-xs text-zinc-400">{customer.phone || 'No phone'}</div>
                      </div>
                      <div className="shrink-0">
                        {customer.blocked ? (
                          <Button
                            size="sm"
                            className="h-8 bg-green-600 hover:bg-green-700 text-white text-xs"
                            onClick={() => unblockCustomer(customer)}
                            data-testid={`unblock-btn-${customer.id}`}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" />Unblock
                          </Button>
                        ) : customer.is_guest && !customer.phone ? (
                          <span className="text-zinc-500 text-[11px] italic">no phone</span>
                        ) : (
                          <Button
                            size="sm"
                            className="h-8 bg-red-600 hover:bg-red-700 text-white text-xs"
                            onClick={() => openBlockDialog(customer)}
                            data-testid={`block-btn-${customer.id}`}
                          >
                            <Ban className="w-3.5 h-3.5 mr-1" />Block
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs pt-2 border-t border-zinc-700">
                      <div>
                        <div className="text-yellow-400 font-bold">{customer.loyalty_points}</div>
                        <div className="text-zinc-500 text-[10px]">Points</div>
                      </div>
                      <div>
                        <div className="text-white font-bold">{customer.total_visits}</div>
                        <div className="text-zinc-500 text-[10px]">Visits</div>
                      </div>
                      <div>
                        <div className={`font-bold ${customer.no_show_count > 0 ? 'text-red-400' : 'text-white'}`}>
                          {customer.no_show_count}
                        </div>
                        <div className="text-zinc-500 text-[10px]">No-Shows</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view (visible md+) */}
              <div className="hidden md:block overflow-x-auto overflow-y-auto max-h-[600px] relative">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-zinc-900 z-10">
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-2 text-white font-semibold">Name</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Email</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Phone</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Membership</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Points</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Visits</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">No-Shows</th>
                      <th className="text-left py-3 px-2 text-white font-semibold">Status</th>
                      <th className="text-right py-3 px-2 text-white font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer, index) => (
                      <tr key={index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="py-3 px-2 font-medium text-white">
                          {customer.name}
                          {customer.is_guest && (
                            <Badge className="ml-2 bg-zinc-700 text-zinc-300 text-[10px] px-1.5 py-0" data-testid={`guest-badge-${customer.id}`}>
                              Guest
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-zinc-300 text-xs">
                          {customer.email || <span className="text-zinc-500 italic">(no email)</span>}
                        </td>
                        <td className="py-3 px-2 text-zinc-300 text-xs">{customer.phone || 'N/A'}</td>
                        <td className="py-3 px-2">
                          {customer.membership_tier === 'vip' ? (
                            <Badge className="bg-gradient-to-r from-lime-500 to-lime-600 text-black">
                              <Crown className="w-3 h-3 mr-1" />
                              VIP
                            </Badge>
                          ) : customer.membership_tier === 'basic' ? (
                            <Badge className="bg-purple-600 text-lime-400">Regular</Badge>
                          ) : (
                            <Badge className="bg-zinc-700 text-zinc-400">None</Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-yellow-400 font-semibold">{customer.loyalty_points}</td>
                        <td className="py-3 px-2 text-white">{customer.total_visits}</td>
                        <td className="py-3 px-2">
                          {customer.no_show_count > 0 ? (
                            <span className="text-red-400 font-semibold">{customer.no_show_count}</span>
                          ) : (
                            <span className="text-white">0</span>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          {customer.blocked ? (
                            <Badge className="bg-red-600/80 text-white" data-testid={`blocked-badge-${customer.id}`}>
                              <Ban className="w-3 h-3 mr-1" />
                              Blocked
                            </Badge>
                          ) : (
                            <Badge className="bg-green-700/40 text-green-300 border border-green-700/40">Active</Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right">
                          {customer.blocked ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-green-600 text-green-400 hover:bg-green-600 hover:text-white"
                              onClick={() => unblockCustomer(customer)}
                              data-testid={`unblock-btn-${customer.id}`}
                            >
                              <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                              Unblock
                            </Button>
                          ) : customer.is_guest && !customer.phone ? (
                            <span className="text-zinc-600 text-xs italic" title="Need a phone number to block a guest">
                              no phone
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                              onClick={() => openBlockDialog(customer)}
                              data-testid={`block-btn-${customer.id}`}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              Block
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Scroll indicator for mobile */}
                {customers.length > 4 && (
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-zinc-900 to-transparent pointer-events-none md:hidden"></div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Barbers Tab */}
        {activeTab === 'barbers' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-white">Barber Performance ({barbers.length})</CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">
                    🟢 Active · 🟡 Low Activity (30+ days) · 🔴 Suspended · ⚫ Archived
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none" data-testid="show-archived-toggle">
                  <input
                    type="checkbox"
                    checked={showArchivedBarbers}
                    onChange={(e) => setShowArchivedBarbers(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 accent-lime-400 cursor-pointer"
                  />
                  Show archived
                </label>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {barbers.map((barber, index) => (
                  <Card
                    key={barber.id || index}
                    className="bg-zinc-800 border-zinc-700"
                    data-testid={`barber-card-${barber.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="flex items-center text-white text-base">
                          <Scissors className="w-5 h-5 mr-2 text-lime-400 flex-shrink-0" />
                          <span className="truncate">{barber.name}</span>
                        </CardTitle>
                        <EngagementStatusBadge
                          status={barber.engagement_status || 'low_activity'}
                          testId={`barber-status-badge-${barber.id}`}
                        />
                      </div>
                      {barber.last_activity_at && barber.engagement_status !== 'suspended' && barber.engagement_status !== 'archived' && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Last activity: {new Date(barber.last_activity_at).toLocaleDateString()}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-white">Total Revenue:</span>
                          <span className="font-semibold text-lime-400">${barber.total_revenue}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white">Appointments:</span>
                          <span className="font-semibold text-white">{barber.total_appointments}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white">Completed:</span>
                          <span className="font-semibold text-blue-400">{barber.completed_appointments}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white">Rating:</span>
                          <span className="font-semibold text-yellow-400">⭐ {barber.avg_rating}</span>
                        </div>

                        {/* Engagement status override controls */}
                        <div className="pt-3 mt-2 border-t border-zinc-700">
                          <label className="block text-xs text-zinc-400 mb-1.5">Engagement Status</label>
                          <select
                            data-testid={`barber-status-select-${barber.id}`}
                            value={barber.engagement_status_override || 'auto'}
                            onChange={(e) => handleEngagementStatusChange(barber.id, e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 text-white text-sm rounded-md px-2 py-1.5 focus:outline-none focus:border-lime-400"
                          >
                            <option value="auto">Auto (from activity)</option>
                            <option value="active">🟢 Force Active</option>
                            <option value="suspended">🔴 Suspend (hide from bookings)</option>
                            <option value="archived">⚫ Archive (no longer with shop)</option>
                          </select>
                          {barber.engagement_status_override && (
                            <p className="text-[10px] text-amber-400 mt-1">
                              Manually overridden — select &quot;Auto&quot; to clear
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* SMS Campaign Tab */}
        {activeTab === 'sms' && (
          <Card className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <Send className="w-5 h-5 mr-2" />
                Send Campaign
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Campaign Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Campaign Type
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setCampaignType('sms')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      campaignType === 'sms'
                        ? 'border-lime-400 bg-lime-500/10 text-lime-400'
                        : 'border-zinc-700 hover:border-zinc-600 text-zinc-400'
                    }`}
                  >
                    <span className="text-2xl mb-2 block">📱</span>
                    SMS Message
                  </button>
                  <button
                    onClick={() => setCampaignType('email')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      campaignType === 'email'
                        ? 'border-lime-400 bg-lime-500/10 text-lime-400'
                        : 'border-zinc-700 hover:border-zinc-600 text-zinc-400'
                    }`}
                  >
                    <span className="text-2xl mb-2 block">📧</span>
                    Email Message
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Send To
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'all', label: 'All Customers' },
                    { value: 'members', label: 'All Members' },
                    { value: 'vip', label: 'VIP Members Only' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setRecipientType(option.value)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        recipientType === option.value
                          ? 'border-lime-400 bg-lime-500/10 text-lime-400'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* SMS Fields */}
              {campaignType === 'sms' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Message (max 160 characters)
                    </label>
                    <Textarea
                      value={smsMessage}
                      onChange={(e) => setSmsMessage(e.target.value.slice(0, 160))}
                      placeholder="🔥 Special offer this week! Get 20% off all services. Book now at (239) 201-8105"
                      className="bg-zinc-800 border-zinc-700 text-white min-h-[120px]"
                      maxLength={160}
                    />
                    <p className="text-xs text-zinc-300 mt-1">{smsMessage.length}/160 characters</p>
                  </div>
                  
                  <Button
                    onClick={handleSendSMS}
                    disabled={smsLoading || !smsMessage.trim()}
                    className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700 font-semibold"
                  >
                    {smsLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send SMS Campaign
                      </>
                    )}
                  </Button>
                  
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-xs text-yellow-300">
                      <strong>Note:</strong> SMS charges apply. Each message costs approximately $0.01-0.02 per recipient.
                    </p>
                  </div>
                </>
              )}
              
              {/* Email Fields */}
              {campaignType === 'email' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Email Subject
                    </label>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Special Offer Just for You!"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Email Message
                    </label>
                    <Textarea
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      placeholder="Hi there! We have an exclusive offer for you this week. Get 20% off all services when you book your next appointment. Call us at (239) 201-8105 or book online!"
                      className="bg-zinc-800 border-zinc-700 text-white min-h-[160px]"
                    />
                    <p className="text-xs text-zinc-300 mt-1">{emailMessage.length} characters</p>
                  </div>
                  
                  <Button
                    onClick={handleSendEmail}
                    disabled={smsLoading || !emailSubject.trim() || !emailMessage.trim()}
                    className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700 font-semibold"
                  >
                    {smsLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email Campaign
                      </>
                    )}
                  </Button>
                  
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-xs text-blue-300">
                      <strong>Note:</strong> Emails will be sent from noreply@booksaucesity.com. Free with Resend (3,000 emails/month).
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Block Customer Dialog */}
      <Dialog
        open={blockDialog.open}
        onOpenChange={(o) => !o && setBlockDialog({ open: false, customer: null })}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white" data-testid="block-customer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-400">
              <Ban className="w-5 h-5 mr-2" />
              Block {blockDialog.customer?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Blocked customers can still log in but will be prevented from booking new appointments.
              They&apos;ll see: <em>&quot;Your account has been restricted. Contact (239) 201-8105.&quot;</em>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Reason <span className="text-red-400">*</span>
              </label>
              <select
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                data-testid="block-reason-select"
              >
                <option value="no_show_repeat">Repeated no-shows</option>
                <option value="disrespectful">Disrespectful behavior</option>
                <option value="payment_issue">Payment issue</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Internal note <span className="text-zinc-500">(optional, admin-only)</span>
              </label>
              <Textarea
                value={blockNote}
                onChange={(e) => setBlockNote(e.target.value)}
                placeholder="Anything you want to remember about this block…"
                className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                data-testid="block-note-textarea"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBlockDialog({ open: false, customer: null })}
              disabled={blockSubmitting}
              data-testid="block-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitBlock}
              disabled={blockSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="block-confirm-btn"
            >
              {blockSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Ban className="w-4 h-4 mr-2" /> Block customer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
